import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Coins,
  Wallet,
  TrendingUp,
  Calculator,
  Info,
  ArrowRight,
  LogOut,
  User,
} from "lucide-react";
import Vertrag from "./Vertrag";
import { useAuth } from "./AuthContext";
import { Link, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const CHF = (v) =>
  new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(v)));

const CHF_KPI = (v) => {
  const val = Math.max(0, Math.round(v || 0));
  if (val >= 1_000_000) {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(val);
  }
  return CHF(val);
};

const NUM = (v, d = 2) =>
  new Intl.NumberFormat("de-CH", { minimumFractionDigits: d, maximumFractionDigits: d }).format(v);

/* ─── Compound interest simulation month by month ─── */
function simulateGrowth({ initial, monthly, years, ratePct, interval }) {
  const rate = ratePct / 100;
  const months = Math.round(years * 12);
  const monthlyRate = interval === "monatlich" ? rate / 12 : 0;
  const yearlyRate = interval === "jährlich" ? rate : 0;

  let capital = initial;
  let contributed = initial;
  const yearly = [{ year: 0, capital: initial, contributed: initial }];

  for (let m = 1; m <= months; m++) {
    capital += monthly;
    contributed += monthly;
    if (interval === "monatlich") {
      capital *= 1 + monthlyRate;
    } else if (interval === "jährlich" && m % 12 === 0) {
      capital *= 1 + yearlyRate;
    }
    if (m % 12 === 0 || m === months) {
      yearly.push({ year: m / 12, capital, contributed });
    }
  }

  return {
    endCapital: capital,
    contributed,
    interestEarned: capital - contributed,
    yearly,
  };
}

/* ─── Withdrawal simulation ─── */
function simulateWithdrawal({ startCapital, monthly, ratePct }) {
  const monthlyRate = ratePct / 100 / 12;
  let capital = startCapital;
  let months = 0;
  const yearly = [{ year: 0, capital }];
  const maxMonths = 100 * 12; // safety
  while (capital > 0 && months < maxMonths) {
    capital = capital * (1 + monthlyRate) - monthly;
    months++;
    if (months % 12 === 0) yearly.push({ year: months / 12, capital: Math.max(0, capital) });
    if (capital <= 0) {
      if (months % 12 !== 0) yearly.push({ year: months / 12, capital: 0 });
      break;
    }
  }
  return {
    months,
    years: months / 12,
    yearly,
    endless: capital > 0 && months >= maxMonths,
  };
}

const Field = ({ label, hint, children, suffix }) => (
  <div className="mb-5">
    <label className="flex items-center gap-2 text-sm font-medium text-ink-900 mb-2">
      {label}
      {hint && (
        <span className="group relative inline-flex" title={hint}>
          <Info className="w-3.5 h-3.5 text-gold-600 cursor-help" />
        </span>
      )}
    </label>
    <div className="relative">
      {children}
      {suffix && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-ink-500 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

const inputClass =
  "w-full px-4 py-3 pr-16 rounded-xl border border-ink-700/15 bg-white text-ink-900 text-base focus:outline-none focus:ring-2 focus:ring-gold-500/50 focus:border-gold-500/40 transition";

const Nav = () => {
  const { user, logout } = useAuth();
  return (
    <header className="max-w-6xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
      <a href="/" data-testid="gold-logo-link" className="flex items-center gap-3">
        <img
          src="/moonshine-logo.png"
          alt="Moonshine Solutions"
          className="h-14 w-14 rounded-full object-cover ring-1 ring-gold-500/30"
        />
        <div className="leading-tight">
          <div className="font-display font-medium text-ink-900 text-[15px] tracking-tight">Moonshine</div>
          <div className="font-sans text-[11px] uppercase tracking-[0.18em] text-ink-500">Solutions</div>
        </div>
      </a>
      <div className="flex items-center gap-6">
        <a
          href="https://moonshine-solutions.com"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="gold-main-site-link"
          className="hidden md:inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-gold-700 transition"
        >
          moonshine-solutions.com
        </a>
        {user && (
          <>
            <span className="hidden md:inline text-xs text-ink-500">{user.email}</span>
            {user.role === "admin" && (
              <a
                href="/backoffice"
                data-testid="gold-backoffice-link"
                className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-gold-700 transition"
              >
                <User className="w-4 h-4" /> Backoffice
              </a>
            )}
            {user.role === "gp" && (
              <a
                href="/partner"
                data-testid="gold-partner-link"
                className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-gold-700 transition"
              >
                <User className="w-4 h-4" /> Mein Bereich
              </a>
            )}
            <button onClick={logout} data-testid="gold-logout" className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-gold-700 transition">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
          </>
        )}
      </div>
    </header>
  );
};

const Gold = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const returnTo = loc.pathname + loc.hash;
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Zinseszins- & Entnahmerechner – Moonshine Solutions";
    // Override existing robots meta so this hidden page is NOT indexed
    const robotsMeta = document.querySelector("meta[name='robots']");
    const prevRobots = robotsMeta?.getAttribute("content");
    if (robotsMeta) robotsMeta.setAttribute("content", "noindex, nofollow");
    return () => {
      document.title = prevTitle;
      if (robotsMeta && prevRobots) robotsMeta.setAttribute("content", prevRobots);
    };
  }, []);

  // Compound inputs (string state → allow empty during editing)
  const [initialStr, setInitialStr] = useState("5000");
  const [monthlyStr, setMonthlyStr] = useState("100");
  const [yearsStr, setYearsStr] = useState("10");
  const [rateStr, setRateStr] = useState("5");
  const [interval, setInterval] = useState("monatlich");

  const initial = Number(initialStr) || 0;
  const monthly = Number(monthlyStr) || 0;
  const years = Math.max(1, Math.min(60, Number(yearsStr) || 0));
  const rate = Number(rateStr) || 0;

  const growth = useMemo(
    () => simulateGrowth({ initial, monthly, years, ratePct: rate, interval }),
    [initial, monthly, years, rate, interval]
  );

  // Withdrawal inputs – prefilled with endCapital
  const [wdStartStr, setWdStartStr] = useState("0");
  const [wdMonthlyStr, setWdMonthlyStr] = useState("500");
  const [wdRateStr, setWdRateStr] = useState("5");
  const [wdManual, setWdManual] = useState(false);
  const [wdRateManual, setWdRateManual] = useState(false);

  useEffect(() => {
    if (!wdManual) setWdStartStr(String(Math.round(growth.endCapital)));
  }, [growth.endCapital, wdManual]);

  // Auto-sync interest rate from Zinseszinsrechner → Entnahmeplan
  useEffect(() => {
    if (!wdRateManual) setWdRateStr(rateStr);
  }, [rateStr, wdRateManual]);

  const wdStart = Number(wdStartStr) || 0;
  const wdMonthly = Number(wdMonthlyStr) || 0;
  const wdRate = Number(wdRateStr) || 0;

  const withdrawal = useMemo(
    () => simulateWithdrawal({ startCapital: wdStart, monthly: wdMonthly, ratePct: wdRate }),
    [wdStart, wdMonthly, wdRate]
  );

  const wdYears = Math.floor(withdrawal.years);
  const wdRestMonths = Math.round((withdrawal.years - wdYears) * 12);

  // Maximum monthly withdrawal that fully preserves the capital
  // (interest exactly covers each month's withdrawal).
  const preserveMax = wdStart * (wdRate / 100 / 12);

  return (
    <div className="min-h-screen bg-ivory-100 text-ink-900 font-sans">
      <Nav />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-4 pb-24">
        {/* Header */}
        <div className="mb-8 sm:mb-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-gold-700 mb-3">
            Kalkulator
          </div>
          <h1 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.02em]">
            Zinseszins &{" "}
            <span className="italic font-serif-display text-gold-gradient">Entnahmeplan.</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-ink-500 max-w-2xl">
            Berechne wie sich dein Kapital über die Zeit entwickelt – und wie lange es
            im Ruhestand tragen wird.
          </p>
        </div>

        {/* ─── SECTION 1: Compound interest ─── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 rounded-3xl bg-white border border-ink-700/10 p-4 sm:p-6 lg:p-10 shadow-sm"
          data-testid="section-zinseszins"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-2xl bg-ink-900 flex items-center justify-center">
              <Coins className="w-5 h-5 text-gold-500" />
            </div>
            <h2 className="font-display text-2xl lg:text-3xl font-medium">Zinseszinsrechner</h2>
          </div>

          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
            {/* Inputs */}
            <div className="lg:col-span-2">
              <Field label="Anfangskapital" hint="Startbetrag, den du heute investierst." suffix="CHF">
                <input
                  type="number"
                  data-testid="zz-initial"
                  min="0"
                  step="500"
                  value={initialStr}
                  onChange={(e) => setInitialStr(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Monatliche Sparrate" hint="Wie viel legst du zusätzlich pro Monat zurück." suffix="CHF">
                <input
                  type="number"
                  data-testid="zz-monthly"
                  min="0"
                  step="50"
                  value={monthlyStr}
                  onChange={(e) => setMonthlyStr(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Spardauer / Anlagezeitraum" hint="Über wie viele Jahre möchtest du sparen." suffix="Jahre">
                <input
                  type="number"
                  data-testid="zz-years"
                  min="1"
                  max="60"
                  step="1"
                  value={yearsStr}
                  onChange={(e) => setYearsStr(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Jährlicher Zinssatz" hint="Erwartete durchschnittliche Rendite pro Jahr." suffix="%">
                <input
                  type="number"
                  data-testid="zz-rate"
                  min="0"
                  max="30"
                  step="0.1"
                  value={rateStr}
                  onChange={(e) => setRateStr(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Ausschüttungsintervall" hint="Wie oft wird der Zins gutgeschrieben (Zinseszins-Effekt).">
                <select
                  data-testid="zz-interval"
                  value={interval}
                  onChange={(e) => setInterval(e.target.value)}
                  className={inputClass + " appearance-none pr-10"}
                >
                  <option value="monatlich">monatlich</option>
                  <option value="jährlich">jährlich</option>
                </select>
              </Field>
            </div>

            {/* Results */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
                <div className="rounded-2xl bg-ivory-100 p-3 sm:p-4 lg:p-5 min-w-0">
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-ink-500 font-semibold mb-1 truncate">
                    Endkapital
                  </div>
                  <div className="font-display text-[13px] sm:text-base md:text-lg xl:text-xl text-gold-gradient font-medium tabular-nums leading-tight break-words" data-testid="zz-endcapital">
                    {CHF_KPI(growth.endCapital)}
                  </div>
                </div>
                <div className="rounded-2xl bg-ivory-100 p-3 sm:p-4 lg:p-5 min-w-0">
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-ink-500 font-semibold mb-1 truncate">
                    Einzahlungen
                  </div>
                  <div className="font-display text-[13px] sm:text-base md:text-lg xl:text-xl text-ink-900 font-medium tabular-nums leading-tight break-words">
                    {CHF_KPI(growth.contributed)}
                  </div>
                </div>
                <div className="rounded-2xl bg-ink-900 text-ivory-100 p-3 sm:p-4 lg:p-5 min-w-0">
                  <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-gold-300 font-semibold mb-1 truncate">
                    Zinsgewinn
                  </div>
                  <div className="font-display text-[13px] sm:text-base md:text-lg xl:text-xl text-gold-gradient font-medium tabular-nums leading-tight break-words">
                    {CHF_KPI(growth.interestEarned)}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-ink-700/10 p-4" style={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={growth.yearly} margin={{ top: 10, right: 15, bottom: 5, left: 0 }}>
                    <CartesianGrid stroke="#E8E0CB" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="year"
                      tick={{ fill: "#4A4A4A", fontSize: 11 }}
                      tickFormatter={(v) => `${v}J`}
                    />
                    <YAxis
                      tick={{ fill: "#4A4A4A", fontSize: 11 }}
                      tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                      width={50}
                    />
                    <Tooltip
                      formatter={(v) => CHF(v)}
                      labelFormatter={(l) => `Nach ${l} Jahren`}
                      contentStyle={{ background: "#0F0F0F", border: "1px solid #C9A961", borderRadius: 8, color: "#FAF8F3" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="contributed"
                      stroke="#A8A8A8"
                      strokeWidth={2}
                      dot={false}
                      name="Einzahlungen"
                    />
                    <Line
                      type="monotone"
                      dataKey="capital"
                      stroke="#C9A961"
                      strokeWidth={3}
                      dot={false}
                      name="Kapital"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 flex justify-center gap-6 text-xs text-ink-500">
                <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-gold-500" /> Kapital (mit Zins)</span>
                <span className="flex items-center gap-2"><span className="w-3 h-0.5 bg-gray-400" /> Einzahlungen</span>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ─── SECTION 2: Withdrawal / Rentenrechner ─── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-white border border-ink-700/10 p-4 sm:p-6 lg:p-10 shadow-sm"
          data-testid="section-entnahmeplan"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-2xl bg-ink-900 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-gold-500" />
            </div>
            <h2 className="font-display text-2xl lg:text-3xl font-medium">Entnahmeplan</h2>
          </div>
          <p className="text-sm text-ink-500 mb-8 ml-14">
            Wie lange reicht dein Kapital, wenn du monatlich einen fixen Betrag entnimmst?
          </p>

          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
            {/* Inputs */}
            <div className="lg:col-span-2">
              <Field
                label={
                  <span className="flex items-center gap-2">
                    Startkapital
                    <button
                      type="button"
                      onClick={() => {
                        setWdManual(false);
                        setWdStartStr(String(Math.round(growth.endCapital)));
                      }}
                      className="text-[10px] uppercase tracking-widest text-gold-700 hover:text-gold-600 underline"
                      data-testid="wd-use-endcapital"
                    >
                      Endkapital übernehmen
                    </button>
                  </span>
                }
                hint="Kapital am Start des Entnahmeplans (z. B. bei Rentenbeginn)."
                suffix="CHF"
              >
                <input
                  type="number"
                  data-testid="wd-start"
                  min="0"
                  step="1000"
                  value={wdStartStr}
                  onChange={(e) => {
                    setWdStartStr(e.target.value);
                    setWdManual(true);
                  }}
                  className={inputClass}
                />
              </Field>
              <Field label="Monatliche Entnahme" hint="Betrag, den du dir monatlich auszahlst." suffix="CHF">
                <input
                  type="number"
                  data-testid="wd-monthly"
                  min="0"
                  step="100"
                  value={wdMonthlyStr}
                  onChange={(e) => setWdMonthlyStr(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field
                label="Zinssatz während Entnahme"
                hint="Erwartete Rendite auf das noch nicht entnommene Kapital."
                suffix="%"
              >
                <input
                  type="number"
                  data-testid="wd-rate"
                  min="0"
                  max="20"
                  step="0.1"
                  value={wdRateStr}
                  onChange={(e) => { setWdRateManual(true); setWdRateStr(e.target.value); }}
                  className={inputClass}
                />
              </Field>
            </div>

            {/* Results */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl bg-ink-900 text-ivory-100 p-4 sm:p-6 mb-4">
                <div className="text-[10px] uppercase tracking-widest text-gold-300 font-semibold mb-2">
                  Dein Kapital reicht
                </div>
                {withdrawal.endless ? (
                  <div className="font-display text-xl sm:text-2xl md:text-3xl text-gold-gradient font-medium leading-tight" data-testid="wd-result">
                    Unendlich · Zinsen decken die Entnahme
                  </div>
                ) : (
                  <div className="font-display text-3xl sm:text-4xl lg:text-5xl text-gold-gradient font-medium leading-none" data-testid="wd-result">
                    {wdYears} Jahre
                    {wdRestMonths > 0 && (
                      <span className="text-xl sm:text-2xl ml-2">{wdRestMonths} Mt.</span>
                    )}
                  </div>
                )}
                <div className="mt-3 text-xs sm:text-sm text-ivory-100/60">
                  Bei {CHF(wdMonthly)} Entnahme / Monat · {NUM(wdRate, 1)} % Rendite
                </div>
              </div>

              {/* Kapitalerhalt Info */}
              <div className="rounded-2xl border border-gold-500/30 bg-ivory-100 p-4 mb-4 flex items-start gap-3" data-testid="wd-preserve-info">
                <div className="mt-0.5 w-8 h-8 rounded-full bg-gold-500/15 flex items-center justify-center shrink-0">
                  <Coins className="w-4 h-4 text-gold-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-gold-700 font-semibold mb-1">
                    Kapitalerhalt · Zinsen decken Entnahme
                  </div>
                  <div className="font-display text-lg sm:text-xl text-ink-900 font-medium tabular-nums leading-tight" data-testid="wd-preserve-amount">
                    max. {CHF(preserveMax)} <span className="text-ink-500 text-sm font-sans">/ Monat</span>
                  </div>
                  <div className="text-xs text-ink-500 mt-1">
                    Bei {CHF(wdStart)} Startkapital und {NUM(wdRate, 1)} % Rendite bleibt dein Kapital dauerhaft erhalten,
                    wenn du monatlich nicht mehr als diesen Betrag entnimmst.
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-ink-700/10 p-4" style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={withdrawal.yearly} margin={{ top: 10, right: 15, bottom: 5, left: 0 }}>
                    <CartesianGrid stroke="#E8E0CB" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="year"
                      tick={{ fill: "#4A4A4A", fontSize: 11 }}
                      tickFormatter={(v) => `${v}J`}
                    />
                    <YAxis
                      tick={{ fill: "#4A4A4A", fontSize: 11 }}
                      tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                      width={50}
                    />
                    <Tooltip
                      formatter={(v) => CHF(v)}
                      labelFormatter={(l) => `Nach ${l} Jahren`}
                      contentStyle={{ background: "#0F0F0F", border: "1px solid #C9A961", borderRadius: 8, color: "#FAF8F3" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="capital"
                      stroke="#C9A961"
                      strokeWidth={3}
                      dot={false}
                      name="Restkapital"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Finanzcheck CTA – zwischen Rechner und Login-Gate */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          data-testid="gold-finanzcheck-cta"
          className="mt-16 rounded-[32px] bg-ivory-200/70 border border-gold-500/25 px-6 md:px-12 py-10 md:py-14 text-center"
        >
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-gold-700 mb-4">
            Deine persönliche Erstanalyse · Ergebnis in wenigen Minuten
          </div>
          <h3 className="font-display text-2xl md:text-4xl font-medium tracking-tight leading-[1.1] max-w-3xl mx-auto">
            Starte jetzt deinen{" "}
            <span className="italic font-serif-display text-gold-gradient">Vorsorge- & Finanzcheck</span>.
          </h3>
          <p className="mt-4 text-sm md:text-base text-ink-700 max-w-2xl mx-auto">
            14 kurze Fragen. Sofort eine persönliche Erstanalyse per E-Mail mit deiner
            CHF-Potenzialrechnung, deiner möglichen Rente ab 65 und den nächsten Schritten.
          </p>
          <a
            href="/finanzcheck"
            data-testid="gold-finanzcheck-cta-button"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-7 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-1 shadow-2xl ring-1 ring-gold-500/30"
          >
            Finanzcheck starten
            <ArrowRight className="w-4 h-4" />
          </a>
          <div className="mt-3 text-xs text-ink-500">3 Minuten · 0.– CHF · vertraulich</div>
        </motion.section>

        {/* Login-Gate: alles unterhalb der Rechner nur für eingeloggte Geschäftspartner */}
        {!user ? (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            data-testid="gold-login-gate"
            className="mt-14 rounded-2xl bg-white border border-ink-700/10 px-6 py-6 md:px-8 md:py-7 flex flex-col md:flex-row md:items-center md:justify-between gap-5"
          >
            <p className="text-sm text-ink-700 max-w-xl leading-relaxed">
              Diese Werkzeuge sind ausschliesslich unseren Geschäftspartnern vorbehalten. Logge dich zuerst ein.
            </p>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Link
                to="/login"
                state={{ from: returnTo }}
                data-testid="gold-gate-login-link"
                className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-5 py-2.5 text-sm font-medium transition"
              >
                <User className="w-4 h-4" /> Als Geschäftspartner einloggen
              </Link>
              <Link
                to="/register"
                state={{ from: returnTo }}
                data-testid="gold-gate-register-link"
                className="inline-flex items-center gap-2 rounded-full bg-transparent border border-ink-700/20 text-ink-700 hover:text-ink-900 hover:border-ink-900/40 px-5 py-2.5 text-sm font-medium transition"
              >
                Noch kein Konto? Registrieren
              </Link>
            </div>
          </motion.section>
        ) : (
          <>
            {/* Vertrags-Wizard */}
            <Vertrag />

            {/* Versand-Hinweis nach Antrag */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              data-testid="gold-versand-hinweis"
              className="mt-16 rounded-3xl bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-ivory-100 px-6 md:px-10 py-8 md:py-10 ring-1 ring-gold-500/25 relative overflow-hidden"
            >
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-gold-500/15 blur-3xl pointer-events-none" />
              <div className="relative flex flex-col md:flex-row md:items-start gap-6">
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-gold-500 text-ink-900 flex items-center justify-center ring-4 ring-gold-500/20">
                  <Info className="w-6 h-6" strokeWidth={2.4} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-gold-300 mb-2">
                    Wichtig · Versand durch den Geschäftspartner
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-medium leading-snug mb-3">
                    Antrag, beglaubigte Ausweiskopie & Geldmittelnachweis sind im Anschluss durch den Geschäftspartner zu senden an:
                  </h3>
                  <a
                    href="mailto:c.laubmann@ips-online.de"
                    data-testid="gold-versand-mail"
                    className="inline-flex items-center gap-2 font-display text-2xl md:text-3xl text-gold-gradient font-medium tracking-tight hover:opacity-90 transition break-all"
                  >
                    c.laubmann@ips-online.de
                  </a>
                  <p className="mt-4 text-sm text-ivory-100/75 max-w-2xl leading-relaxed">
                    Bitte alle drei Dokumente gebündelt einreichen: den ausgefüllten <strong className="text-ivory-100">Antrag</strong>, die <strong className="text-ivory-100">beglaubigte Ausweiskopie</strong> und den <strong className="text-ivory-100">Geldmittelnachweis</strong>. Erst dann kann die Bearbeitung starten.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* Zusatz-Werkzeug: Ausweisbeglaubigung */}
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
              className="mt-20 rounded-3xl bg-white border border-ink-700/10 p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
            >
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-gold-700 mb-2">Zusatz-Werkzeug</div>
                <h3 className="font-display text-2xl md:text-3xl font-medium mb-2">Ausweiskopie beglaubigen</h3>
                <p className="text-sm text-ink-700 max-w-2xl">
                  Lade Vorder- und Rückseite der Identitätskarte (oder eine Passseite) hoch und erstelle
                  ein sauberes PDF mit deiner Bestätigung und Unterschrift – in unter zwei Minuten.
                </p>
              </div>
              <a
                href="/ID"
                data-testid="gold-id-tool-link"
                className="inline-flex items-center gap-2 rounded-full bg-ink-900 hover:bg-ink-700 text-gold-300 px-6 py-3 text-sm font-medium transition"
              >
                Tool öffnen <ArrowRight className="w-4 h-4" />
              </a>
            </motion.section>
          </>
        )}

        <p className="mt-10 text-xs text-ink-500 leading-relaxed max-w-3xl">
          Alle Berechnungen sind unverbindliche Modellrechnungen und basieren auf
          konstanter Renditeannahme. Tatsächliche Erträge können deutlich
          abweichen. Diese Werkzeuge stellen keine Anlageberatung dar.
        </p>
      </main>
    </div>
  );
};

export default Gold;
